import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Collapse, Empty, Spin, Tag, message } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import tipOfferApi from "api/tipOfferApi";
import { GOLFER_ROUTES, buildTipOfferDetailPath } from "constants/routesPath";
import {
  GENERIC_ERROR,
  OFFER_STATUS,
  SELECTION_CUTOFF_MINUTES,
  apiErrorMessage,
  formatCurrency,
  formatOfferDate,
  formatOfferDateTime,
  formatOfferTime,
  statusLabel,
} from "utils/tipOffer";

// Minimum tap-target size for accessibility (spec: >= 44px).
const TAP_TARGET = 44;

const HOW_IT_WORKS = [
  {
    th: "เลือกสนาม วันและเวลาทีออฟ แล้วตั้งทิปที่คุณยินดีจ่าย",
    en: "Pick the golf course, tee-off date and time, and the tip you are willing to pay.",
  },
  {
    th: "แคดดี้ที่ว่างจะได้รับแจ้งเตือนทาง LINE และสมัครเข้ามา",
    en: "Available caddies are notified on LINE and apply for your round.",
  },
  {
    th: "คุณจะได้รับแจ้งเตือนทาง LINE เมื่อมีแคดดี้สมัคร ดูโปรไฟล์แล้วเลือกคนที่ใช่",
    en: "You get a LINE message when a caddie applies. Check their profiles and choose one.",
  },
  {
    th: "ชำระเงินเพื่อยืนยันการจอง",
    en: "Pay to confirm the booking.",
  },
];

function StepNumber({ n }) {
  return (
    <span
      style={{
        flex: "none",
        width: 24,
        height: 24,
        borderRadius: "50%",
        background: "#07C756",
        color: "white",
        fontWeight: "bold",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {n}
    </span>
  );
}

function OfferDetail({ label, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ color: "#666" }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function HowItWorks() {
  return (
    <div>
      {HOW_IT_WORKS.map((step, i) => (
        <div key={i} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <StepNumber n={i + 1} />
          <div>
            <div>{step.th}</div>
            <div style={{ color: "#666", fontSize: 13 }}>{step.en}</div>
          </div>
        </div>
      ))}
      <div
        style={{
          background: "#f6f6f6",
          borderRadius: 10,
          padding: "8px 12px",
          fontSize: 13,
          color: "#444",
        }}
      >
        ต้องเลือกแคดดี้ก่อนเวลาทีออฟอย่างน้อย {SELECTION_CUTOFF_MINUTES} นาที
        หากไม่ได้เลือก ข้อเสนอจะหมดเวลา คุณยกเลิกข้อเสนอที่ยังเปิดอยู่ได้ทุกเมื่อ
        <br />
        Choose a caddie at least {SELECTION_CUTOFF_MINUTES} minutes before
        tee-off, or the offer expires. You can cancel an open offer at any time.
      </div>
    </div>
  );
}

/**
 * "Book me a caddie" — the golfer's own offers (internal: tip-offer), newest
 * first, with a button to create a new one. Each offer opens its detail page.
 */
export default function TipOfferList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [offers, setOffers] = useState([]);
  // "How it works" starts open for golfers with no offers yet, and collapsed
  // once they have some so the list stays near the top.
  const [howOpen, setHowOpen] = useState(null);

  const fetchOffers = useCallback(async () => {
    setLoadError(null);
    try {
      const response = await tipOfferApi.getMyOffers();
      const list = response?.data?.data || [];
      setOffers(list);
      setHowOpen((open) => (open === null ? list.length === 0 : open));
    } catch (error) {
      console.error("Failed to load tip offers:", error);
      setLoadError(apiErrorMessage(error, GENERIC_ERROR));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Success message handed over by the create page. Clear it from history so a
  // refresh or back navigation doesn't show it again.
  const successMessage = location.state?.successMessage;
  useEffect(() => {
    if (!successMessage) return;
    // A fixed key keeps StrictMode's double effect run to one message.
    messageApi.success({ content: successMessage, key: "tip-offer-created" });
    navigate(location.pathname, { replace: true, state: null });
  }, [successMessage, messageApi, navigate, location.pathname]);

  return (
    <div style={{ padding: 16, maxWidth: 600, margin: "0 auto" }}>
      {contextHolder}
      <h2 style={{ marginBottom: 4 }}>จองแคดดี้ให้ฉัน</h2>
      <p style={{ marginTop: 0, color: "#666" }}>
        Book me a caddie — ไม่ต้องหาแคดดี้เอง ตั้งทิปไว้
        แล้วให้แคดดี้ที่ว่างสมัครเข้ามาหาคุณ
        <br />
        No need to search. Set a tip and let available caddies come to you.
      </p>

      <Collapse
        activeKey={howOpen ? ["how"] : []}
        onChange={(keys) => setHowOpen(keys.length > 0)}
        style={{ marginBottom: 16, background: "white" }}
        items={[
          {
            key: "how",
            label: <b>ใช้งานอย่างไร (How it works)</b>,
            children: <HowItWorks />,
          },
        ]}
      />

      <Button
        type="primary"
        icon={<PlusOutlined />}
        block
        style={{ height: TAP_TARGET, marginBottom: 16 }}
        onClick={() => navigate(GOLFER_ROUTES.TIP_OFFER_CREATE)}
      >
        สร้างข้อเสนอใหม่ (New offer)
      </Button>

      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin indicator={<LoadingOutlined spin />} />
        </div>
      ) : loadError ? (
        <div style={{ textAlign: "center" }}>
          <p role="alert">
            {loadError.th}
            <br />
            {loadError.en}
          </p>
          <Button
            style={{ height: TAP_TARGET }}
            onClick={() => {
              setLoading(true);
              fetchOffers();
            }}
          >
            ลองใหม่ (Retry)
          </Button>
        </div>
      ) : offers.length === 0 ? (
        <Empty description="ยังไม่มีข้อเสนอ (No offers yet)" />
      ) : (
        offers.map((offer) => {
          const status = statusLabel(OFFER_STATUS, offer.status);
          return (
            <Card
              key={offer.id}
              hoverable
              role="button"
              tabIndex={0}
              style={{ marginBottom: 10 }}
              onClick={() => navigate(buildTipOfferDetailPath(offer.id))}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(buildTipOfferDetailPath(offer.id));
              }}
              data-testid={`offer-${offer.id}`}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <b>{offer.golf_course_name || "-"}</b>
                <Tag color={status.color}>
                  {status.th} ({status.en})
                </Tag>
              </div>
              <div style={{ marginTop: 6 }}>
                <OfferDetail label="วันเวลาทีออฟ (Tee-off)">
                  {formatOfferDate(offer.tf_date)} · {formatOfferTime(offer.tf_time)}
                </OfferDetail>
                <OfferDetail label="ทิป (Tip)">
                  {formatCurrency(offer.tip_amount)} บาท
                </OfferDetail>
                <OfferDetail label="ผู้สมัคร (Applicants)">
                  {offer.applicant_count ?? 0} คน
                </OfferDetail>
              </div>
              <div style={{ color: "#999", fontSize: 12, marginTop: 6 }}>
                สร้างเมื่อ (Created) {formatOfferDateTime(offer.created_at)}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
